import { TFunction } from 'i18next';
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_TYPE,
} from 'services/notifications/types/enums';
import { INotificationContent } from 'services/notifications/types/types';
import { formatDate } from 'utils/global/date.utils';

const DELIVERY_NOTIFICATION_TYPES = new Set([
  NOTIFICATION_TYPE.CreateDelivery,
  NOTIFICATION_TYPE.UpdateDelivery,
  NOTIFICATION_TYPE.DeleteDelivery,
  NOTIFICATION_TYPE.DeliveryChangedStatus,
]);

const TEMPLATE_NOTIFICATION_ROUTES: Partial<
  Record<NOTIFICATION_TYPE, (entityId: string) => string>
> = {
  [NOTIFICATION_TYPE.CreateAgreementTemplate]: id =>
    `/templates-extended/agreement/${id}`,
  [NOTIFICATION_TYPE.UpdateAgreementTemplate]: id =>
    `/templates-extended/agreement/${id}`,
  [NOTIFICATION_TYPE.DeleteAgreementTemplate]: id =>
    `/templates-extended/agreement/${id}`,
  [NOTIFICATION_TYPE.CreateContractTemplate]: id => `/templates/contract/${id}`,
  [NOTIFICATION_TYPE.UpdateContractTemplate]: id => `/templates/contract/${id}`,
  [NOTIFICATION_TYPE.DeleteContractTemplate]: id => `/templates/contract/${id}`,
  [NOTIFICATION_TYPE.CreateCustomSectionTemplate]: id =>
    `/templates-extended/section/${id}`,
  [NOTIFICATION_TYPE.UpdateCustomSectionTemplate]: id =>
    `/templates-extended/section/${id}`,
  [NOTIFICATION_TYPE.DeleteCustomSectionTemplate]: id =>
    `/templates-extended/section/${id}`,
  [NOTIFICATION_TYPE.CreateDigitalDistributionSectionTemplate]: id =>
    `/templates-extended/digital-distribution/${id}`,
  [NOTIFICATION_TYPE.UpdateDigitalDistributionSectionTemplate]: id =>
    `/templates-extended/digital-distribution/${id}`,
  [NOTIFICATION_TYPE.DeleteDigitalDistributionSectionTemplate]: id =>
    `/templates-extended/digital-distribution/${id}`,
  [NOTIFICATION_TYPE.CreatePhysicalDistributionSectionTemplate]: id =>
    `/templates-extended/physical-distribution/${id}`,
  [NOTIFICATION_TYPE.UpdatePhysicalDistributionSectionTemplate]: id =>
    `/templates-extended/physical-distribution/${id}`,
  [NOTIFICATION_TYPE.DeletePhysicalDistributionSectionTemplate]: id =>
    `/templates-extended/physical-distribution/${id}`,
};

const CONFIGURATION_NOTIFICATION_ROUTES: Partial<
  Record<NOTIFICATION_TYPE, (entityId: string) => string>
> = {
  [NOTIFICATION_TYPE.CreateUserConfiguration]: () => '/settings',
  [NOTIFICATION_TYPE.CreateLicenceConfiguration]: () => '/settings/license',
  [NOTIFICATION_TYPE.CreateEmailConfiguration]: id => `/settings/email/${id}`,
  [NOTIFICATION_TYPE.CreateApplicationConfiguration]: () =>
    '/settings/application',
};

type PermissionCheck = (
  permissionName: string,
  permissionFunctionality?: number
) => boolean;

interface INotificationRouteConfig {
  path: string;
  permission?: string;
  permissionBit?: number;
}

export const isDeliveryNotification = (notificationType: number): boolean =>
  DELIVERY_NOTIFICATION_TYPES.has(notificationType);

const getEntityIdValue = (
  entityIds: INotificationContent['entityIds'],
  key: string
): string => entityIds?.[key]?.id ?? '';

const getEntityNameValue = (
  entityIds: INotificationContent['entityIds'],
  key: string
): string => entityIds?.[key]?.entityName ?? '';

export const getDeliveryProductId = (item: INotificationContent): string =>
  getEntityIdValue(item.entityIds, 'ProductId') ||
  getEntityIdValue(item.entityIds, 'ProductName') ||
  getEntityIdValue(item.entityIds, 'Id');

export const getDeliveryProductName = (item: INotificationContent): string =>
  getEntityNameValue(item.entityIds, 'ProductId') ||
  getEntityNameValue(item.entityIds, 'ProductName') ||
  getEntityNameValue(item.entityIds, 'Id');

export const getNotificationEntityId = (item: INotificationContent): string => {
  if (isDeliveryNotification(item.notificationType)) {
    return getDeliveryProductId(item);
  }

  return getEntityIdValue(item.entityIds, 'Id');
};

export const getNotificationDisplayName = (
  item: INotificationContent
): string => {
  if (isDeliveryNotification(item.notificationType)) {
    return getDeliveryProductName(item) || '-';
  }

  return getEntityNameValue(item.entityIds, 'Id') || '-';
};

export const getNotificationDateSource = (item: INotificationContent): string =>
  item.createdAt ?? item.lastStatusChangeDate;

export const formatNotificationTime = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

export const formatNotificationDate = (dateString: string): string =>
  formatDate(dateString);

export const getNotificationReceivedTypeLabel = (
  t: TFunction,
  notificationType: number
): string => {
  const typeKey = NOTIFICATION_TYPE[notificationType];

  if (!typeKey) {
    return '-';
  }

  return t(`notification.received.type.${typeKey}`, {
    defaultValue: t(`enum.NOTIFICATION_TYPE.${typeKey}`, typeKey),
  });
};

const getTemplateRoute = (
  notificationType: number,
  entityId: string
): string | null => {
  const routeBuilder =
    TEMPLATE_NOTIFICATION_ROUTES[notificationType as NOTIFICATION_TYPE];

  return routeBuilder && entityId ? routeBuilder(entityId) : null;
};

const getConfigurationRoute = (
  notificationType: number,
  entityId: string
): string | null => {
  const routeBuilder =
    CONFIGURATION_NOTIFICATION_ROUTES[notificationType as NOTIFICATION_TYPE];

  return routeBuilder ? routeBuilder(entityId) : null;
};

export const getNotificationRouteConfig = (
  item: INotificationContent
): INotificationRouteConfig | null => {
  const entityId = getNotificationEntityId(item);
  const { contentCategory, notificationType } = item;

  if (isDeliveryNotification(notificationType) && entityId) {
    return {
      path: `/music-product-digital/${entityId}/delivery`,
      permission: 'DigitalDeliveryPermissionsLIST**',
      permissionBit: 16,
    };
  }

  const templateRoute = getTemplateRoute(notificationType, entityId);
  if (templateRoute) {
    return {
      path: templateRoute,
      permission: 'ContractTemplatePermissionsLIST**',
      permissionBit: 16,
    };
  }

  const configurationRoute = getConfigurationRoute(notificationType, entityId);
  if (configurationRoute) {
    return {
      path: configurationRoute,
    };
  }

  switch (contentCategory) {
    case NOTIFICATION_CATEGORY.Labels:
      return entityId
        ? {
            path: `/label/${entityId}/finances`,
            permission: 'LabelPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Contracts:
      return { path: '/contracts', permission: 'ContractPermissionsLIST**' };

    case NOTIFICATION_CATEGORY.Youtube:
    case NOTIFICATION_CATEGORY.Channel:
      return entityId
        ? {
            path: `/channel/${entityId}`,
            permission: 'ChannelPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Settlements:
      return {
        path: '/settlement',
        permission: 'SettlementPermissionsLIST**',
      };

    case NOTIFICATION_CATEGORY.Statements:
      return entityId
        ? {
            path: `/statements/${entityId}`,
            permission: 'StatementPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.LabelReports:
      return {
        path: '/label-reports',
        permission: 'LabelReportOrderPermissionsLIST**',
      };

    case NOTIFICATION_CATEGORY.Artists:
      return entityId
        ? {
            path: `/artist/${entityId}/external`,
            permission: 'ArtistPermissionsLIST**',
            permissionBit: 128,
          }
        : null;

    case NOTIFICATION_CATEGORY.Tracks:
      return entityId
        ? {
            path: `/track/${entityId}/file`,
            permission: 'TrackPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.MusicProductDigital:
      return entityId
        ? {
            path: `/music-product-digital/${entityId}`,
            permission: 'DigitalMusicProductPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.SalesChannel:
      return entityId
        ? {
            path: `/settings/sales-channel/${entityId}`,
            permission: 'SalesChannelPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Contract:
      return {
        path: '/contracts',
        permission: 'ContractPermissionsLIST**',
      };

    case NOTIFICATION_CATEGORY.Video:
      return entityId
        ? {
            path: `/channel/${entityId}`,
            permission: 'ChannelPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Merch:
      return entityId
        ? {
            path: `/merch/${entityId}`,
            permission: 'MerchPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Provider:
      return entityId
        ? {
            path: `/settings/provider/${entityId}`,
            permission: 'ProvidersPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.MusicProductPhysical:
      return entityId
        ? {
            path: `/music-product-physical/${entityId}`,
            permission: 'PhysicalMusicProductPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Videos:
      return entityId
        ? {
            path: `/video/${entityId}`,
            permission: 'VideoPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.Events:
      return entityId
        ? {
            path: `/event/${entityId}`,
            permission: 'EventPermissionsLIST**',
            permissionBit: 16,
          }
        : null;

    case NOTIFICATION_CATEGORY.SpotifyStatistics:
    case NOTIFICATION_CATEGORY.YoutubeStatistics:
      return {
        path: '/',
        permission: 'StatisticsPermissionsLIST**',
      };

    case NOTIFICATION_CATEGORY.Configurations:
      return {
        path: '/settings',
      };

    default:
      return null;
  }
};

export const canNavigateToNotificationEntity = (
  item: INotificationContent,
  hasPermission: PermissionCheck
): boolean => {
  const routeConfig = getNotificationRouteConfig(item);

  if (!routeConfig?.path) {
    return false;
  }

  if (!routeConfig.permission) {
    return true;
  }

  return hasPermission(
    routeConfig.permission,
    routeConfig.permissionBit ?? 16
  );
};

export const getNotificationNavigationPath = (
  item: INotificationContent,
  hasPermission: PermissionCheck
): string | null => {
  if (!canNavigateToNotificationEntity(item, hasPermission)) {
    return null;
  }

  return getNotificationRouteConfig(item)?.path ?? null;
};
